<template>
    <div>
        <v-row>
            <v-col>
                <h4 class="text-h4">Customer Payments</h4>
                <p class="text-subtitle-1">List of customer payments</p>
                <v-divider></v-divider>
            </v-col>
        </v-row>
        <v-row>
            <v-col>
                <v-btn
                    block
                    color="primary"
                    :disabled="authorizations.payments ? !authorizations.payments.create : true"
                    @click="openIncomingPayment(false)">
                    <v-icon left>mdi-database-plus</v-icon>
                    {{ $t('new_payment') }}
                </v-btn>
            </v-col>
            <v-col>
                <v-menu
                    offset-y
                    :close-on-content-click="false"
                    v-model="display.search">
                    <template v-slot:activator="{on}">
                        <v-btn
                            block
                            outlined
                            v-on="on">
                            <v-icon left>mdi-magnify</v-icon>
                            search
                        </v-btn>
                    </template>
                    <PaymentSearchMenu
                        :loading="loading.data"
                        v-model="searchForm"
                        @submit="onPaymentSearchMenuSubmit"/>
                </v-menu>
            </v-col>
            <v-col>
                <v-btn 
                    block
                    depressed
                    @click="refreshDataFetch">
                    <v-icon left>mdi-refresh</v-icon>
                    refresh
                </v-btn>
            </v-col>
            <v-spacer></v-spacer>
            <v-col>
                <v-btn
                    block
                    depressed
                    color="warning"
                    :disabled="!selected.payment[0].meta.editable"
                    v-if="selected.payment.length == 1 && authorizations.payments ? authorizations.payments.update : false"
                    @click="openIncomingPayment(true)">
                    <v-icon left>mdi-pencil</v-icon>
                    edit
                </v-btn>
            </v-col>
            <v-col>
                <v-btn
                    block
                    depressed
                    color="error"
                    :disabled="!selected.payment[0].meta.deletable"
                    v-if="selected.payment.length && authorizations.payments ? authorizations.payments.delete : false"
                    @click="openDeleteSingleDialog({target: 'payment', id: selected.payment[0].id})">
                    <v-icon left>mdi-delete</v-icon>
                    delete
                </v-btn>
            </v-col>
        </v-row>
        <v-row>
            <v-col>
                <payment-data-table
                    ref="invoicesDataTable"
                    :data="payments"
                    :loading="loading.data"
                    :length="paymentsLength"
                    :pagination="true"
                    :page.sync="page"
                    :options.sync="options"
                    @selected:row="setSelected"
                    @selected:clear="clearSelected"
                    @updated="handleDataTableOptionsUpdate"
                    @updated:payment="handlePaymentUpdate">
                </payment-data-table>
            </v-col>
        </v-row>
        <RetryDialog
            ref="retryDialog"
            @submit="onRetryDialogSubmit"/>
        <PaymentDialog
            @submit="refreshDataFetch"
            ref="paymentDialog"/>
        <AlertDialog 
            ref="alertDialog"/>
        <DeleteSingleDialog
            @submit="onDeleteDialogSubmit"
            ref="deleteSingleDialog"/>
        <PaymentDisplayDialog
            ref="paymentDisplayDialog"/>
        <incoming-payment
            v-if="incomingPayment.display"
            :id="incomingPayment.id"
            @update="refreshDataFetch"
            @close="incomingPayment.display = false">
        </incoming-payment>
    </div>
</template>

<script>
import accounting from 'accounting';
import { mapState, mapActions } from 'vuex';

import RetryDialog from '@/components/common/RetryDialog';
import PaymentDialog from '@/components/Sales/PaymentDialog';
import AlertDialog from '@/components/common/AlertDialog';
import DeleteSingleDialog from '@/components/common/DeleteSingleDialog';
import PaymentDisplayDialog from '@/components/Sales/PaymentDisplayDialog';
import PaymentSearchMenu from '../../components/Sales/PaymentSearchMenu';
import IncomingPayment from '../../components/accounting/IncomingPayment.vue';
import PaymentDataTable from '../../components/Sales/PaymentDataTable.vue';
export default {
    name: "Payments",
    components: {
        RetryDialog,
        PaymentDialog,
        AlertDialog,
        DeleteSingleDialog,
        PaymentDisplayDialog,
        PaymentSearchMenu,
        IncomingPayment,
        PaymentDataTable
    },
    data() {
        return {
            paymentsLength: 0,
            payments: [],
            loading: {
                data: false            
            },
            selected: {
                payment: []
            },
            incomingPayment: {
                id: null,
                display: false
            },
            display: {
                search: false
            },
            headers: {
                payment: [
                    { text: this.$t('code'), value: "code" },
                    { text: this.$t('date'), value: "date" },
                    { text: this.$t('customer'), sortable: false, value: "customer" },
                    { text: this.$t('mode'), sortable: false, value: "payment_mode" },
                    { text: this.$t('amount'), value: "amount" },
                    { text: this.$t('state'), value: 'state' }
                ]
            },
            search: null,
            options: {},
            searchForm: {
                payment_code: null,
                customer_id: null,
                date_from: null,
                date_to: null,
                user_id: null,
                payment_mode: null,
                amount: null,
                description: null,
                state:null,
            },
            page: 1
        }
    },
    computed: {
        ...mapState('authentication', ["authorizations"])
    },
    watch: {
        options: {
            deep: true,
            handler() {
                this.getPayment()
            }
        },
    },
    methods: {
        formatNumber(number) {
            return accounting.formatMoney(number, "SDG ", 2);
        },
        ...mapActions("sales/payment",["fetchPayments", "removePayment"]),
        handleDataTableOptionsUpdate(options) {
            this.options = options;
            this.getPayment();
        },
        async getPayment() {
            this.loading.data = true ;
            let result = await this.fetchPayments({...this.options, ...this.searchForm});
            this.payments = result.data.data;
            this.paymentsLength = result.data.total;
            this.loading.data = false;
        },
        setSelected(payment) {
            this.selected.payment = payment;
        },
        clearSelected() {
            this.selected.payment = [];
        },
        openPaymentDialog(params) {
            this.$refs.paymentDialog.open(params);
        },
        refreshDataFetch() {
            this.search = null;
            this.getPayment();
        },
        openDeleteSingleDialog(params) {
            this.$refs.deleteSingleDialog.open(params);
        },
        getColor(state) {
            if(state == 'DRAFT') return 'error';
            else return 'success';
        },
        onDeleteDialogSubmit(params) {
            if(params.target == 'payment') {
                this.removePayment(params)
                    .then(() => {
                        this.$refs.deleteSingleDialog.close();
                        this.getPayment();
                        this.$refs.alertDialog.open({type: "success", message: this.$t("records_delete_success")});
                    })
                    .catch((error) => {
                        this.$refs.deleteSingleDialog.close();
                        let message = error.response ? error.response.data.message : error;
                        this.$refs.alertDialog.open({type: "error", message});
                    })
            }
        },
        onRetryDialogSubmit(params){
            if(params.target == "createdDataFetch") {
                if(params.retry) this.createdDataFetch();
                else this.close()
            }
        },
        openIncomingPayment(edit){
            this.incomingPayment.id = edit ? this.selected.payment[0].id : null;
            this.incomingPayment.display = true;
        },
        openPaymentDisplayDialog(item){
            this.$refs.paymentDisplayDialog.open(item);
            this.selected.payment = [];
        },
        onPaymentSearchMenuSubmit() {
            this.page = 1;
            this.getPayment();
        },
        handlePaymentUpdate() {
            this.getPayment();
        }
    }
}
</script>